import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Trophy, Loader2 } from "lucide-react";
import { GeneratedMatch, AssignedMatch } from '@/types/courtAssignment';
import { cn } from "@/lib/utils";

interface MatchScore {
  winner: string;
  sets: Array<{ team1: number; team2: number }>;
  score: string;
  duration?: string;
}

interface ScoreAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  match: GeneratedMatch;
  assignedMatch: AssignedMatch;
  setsPerMatch: number;
  superTieBreak?: boolean;
  maxGamesPerSet?: number;
  isDoublesLadder?: boolean;
  isRelampago?: boolean;
  compactMode?: boolean;
  strictSaveMode?: boolean;
  onSaveScore: (matchId: string, scoreData: MatchScore) => void | Promise<void>;
  initialScore?: {
    sets: Array<{ team1: number; team2: number }>;
    winner: 'team1' | 'team2' | null;
  };
}

const ScoreAssignmentDialog: React.FC<ScoreAssignmentDialogProps> = ({
  open,
  onOpenChange,
  match,
  assignedMatch,
  setsPerMatch,
  superTieBreak = false,
  maxGamesPerSet = 7,
  isDoublesLadder = false,
  isRelampago = false,
  compactMode = false,
  strictSaveMode = false,
  onSaveScore,
  initialScore
}) => {
  // Detectar si es fase de eliminación
  const eliminationPhases = ['QF', 'SF', 'Final', 'Cuartos', 'Semifinal', 'Octavos', 'Oct', 'R16', '16'];
  const isEliminationPhase = assignedMatch.phase !== 'Grupos' && 
    eliminationPhases.some(p => assignedMatch.phase?.toLowerCase().includes(p.toLowerCase()));
  
  // Detect if this is a Relámpago group phase match
  const isRelampagoGroupPhase = isRelampago && !isEliminationPhase;
  
  // UI simplificada para eliminación O ligas escalera dobles
  const useSimplifiedUI = isEliminationPhase || isDoublesLadder;
  
  // Validar y asegurar que setsPerMatch sea correcto
  // For Relámpago group phase: force 1 set
  const validSetsPerMatch = isRelampagoGroupPhase ? 1 : ((setsPerMatch === 1 || setsPerMatch === 3) ? setsPerMatch : 3);
  
  // For Relámpago group phase: no max restriction on game scores
  const effectiveMaxGamesPerSet = isRelampagoGroupPhase ? 99 : maxGamesPerSet;
  
  console.log('📊 ScoreAssignmentDialog - phase:', assignedMatch.phase, 'isElimination:', isEliminationPhase, 'isDoublesLadder:', isDoublesLadder);
  
  const [sets, setSets] = useState<Array<{ team1: number | string; team2: number | string }>>([
    { team1: 0, team2: 0 },
    { team1: 0, team2: 0 },
    { team1: 0, team2: 0 }
  ]);
  const [errors, setErrors] = useState<string[]>([]);
  const [selectedWinner, setSelectedWinner] = useState<'team1' | 'team2' | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (initialScore && initialScore.sets.length > 0) {
        const prefilled = [
          initialScore.sets[0] || { team1: 0, team2: 0 },
          initialScore.sets[1] || { team1: 0, team2: 0 },
          initialScore.sets[2] || { team1: 0, team2: 0 }
        ];
        setSets(prefilled);
        setSelectedWinner(initialScore.winner);
      } else {
        setSets([
          { team1: 0, team2: 0 },
          { team1: 0, team2: 0 },
          { team1: 0, team2: 0 }
        ]);
        setSelectedWinner(null);
      }
      setErrors([]);
      setIsSaving(false);
    }
  }, [open, initialScore]);

  const validateSetScore = (score1: number, score2: number, isSuperTieBreak: boolean = false): boolean => {
    // Relámpago group phase: allow any scores including ties
    if (isRelampagoGroupPhase) {
      if (score1 === 0 && score2 === 0) return true;
      return true;
    }
    
    const maxScore = isSuperTieBreak ? 15 : effectiveMaxGamesPerSet;
    
    if (score1 < 0 || score2 < 0 || score1 > maxScore || score2 > maxScore) {
      return false;
    }
    
    if (score1 === 0 && score2 === 0) {
      return true;
    }
    
    if (isSuperTieBreak) {
      const diff = Math.abs(score1 - score2);
      const maxOfBoth = Math.max(score1, score2);
      return maxOfBoth >= 10 && diff >= 2;
    }
    
    if (effectiveMaxGamesPerSet > 7) {
      return score1 !== score2;
    }
    
    const diff = Math.abs(score1 - score2);
    if (score1 === 7 || score2 === 7) {
      return diff >= 1;
    }
    
    if (score1 >= 6 || score2 >= 6) {
      return diff >= 2;
    }
    
    return true;
  };

  const calculateWinner = (playedSets: Array<{ team1: number; team2: number }>): string | null => {
    let team1Wins = 0;
    let team2Wins = 0;
    
    playedSets.forEach(set => {
      if (set.team1 > set.team2) team1Wins++;
      else if (set.team2 > set.team1) team2Wins++;
    });
    
    if (isRelampagoGroupPhase && team1Wins === team2Wins) return 'draw';
    
    if (validSetsPerMatch === 3) {
      if (team1Wins >= 2) return 'team1';
      if (team2Wins >= 2) return 'team2';
      return null;
    }
    
    return team1Wins > team2Wins ? 'team1' : 'team2';
  };

  const formatScore = (
    playedSets: Array<{ team1: number; team2: number }>, 
    winner: string | null
  ): string => {
    return playedSets
      .filter(set => set.team1 > 0 || set.team2 > 0)
      .map(set => {
        if (winner === 'team2') {
          return `${set.team2}-${set.team1}`;
        }
        return `${set.team1}-${set.team2}`;
      })
      .join(', ');
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (useSimplifiedUI) {
      if (!selectedWinner) {
        setErrors(['Debes seleccionar un equipo ganador']);
        return;
      }
      
      const scoreData: MatchScore = {
        winner: selectedWinner,
        sets: [],
        score: 'Ganador'
      };
      
      try {
        setIsSaving(true);
        await onSaveScore(match.id, scoreData);
      } finally {
        setIsSaving(false);
      }
      return;
    }
    
    const newErrors: string[] = [];
    
    const normalizedSets = sets.slice(0, validSetsPerMatch).map(set => ({
      team1: typeof set.team1 === 'string' ? 0 : set.team1,
      team2: typeof set.team2 === 'string' ? 0 : set.team2,
    }));
    const playedSets = normalizedSets.filter(set => set.team1 > 0 || set.team2 > 0);
    
    if (playedSets.length === 0) {
      newErrors.push('Debes completar al menos un set');
    }
    
    playedSets.forEach((set, index) => {
      const isSuperTieBreakSet = index === 2 && superTieBreak && validSetsPerMatch === 3;
      if (!validateSetScore(set.team1, set.team2, isSuperTieBreakSet)) {
        const setLabel = isSuperTieBreakSet ? 'Super Muerte' : `Set ${index + 1}`;
        newErrors.push(`${setLabel}: El score no es válido`);
      }
    });
    
    if (validSetsPerMatch === 3 && playedSets.length > 0) {
      const team1Wins = playedSets.filter(s => s.team1 > s.team2).length;
      const team2Wins = playedSets.filter(s => s.team2 > s.team1).length;
      
      if (playedSets.length >= 2 && team1Wins < 2 && team2Wins < 2 && team1Wins === team2Wins) {
        newErrors.push('En mejor de 3, el ganador debe ganar 2 sets');
      }
    }
    
    if (newErrors.length > 0) {
      setErrors(newErrors);
      return;
    }
    
    const winner = calculateWinner(playedSets);
    const score = formatScore(playedSets, winner);
    
    const scoreData: MatchScore = {
      winner,
      sets: playedSets,
      score
    };
    
    try {
      setIsSaving(true);
      await onSaveScore(match.id, scoreData);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetChange = (setIndex: number, team: 'team1' | 'team2', value: string) => {
    const numValue = value === '' ? '' : (parseInt(value) || 0);
    const isSuperTieBreakSet = setIndex === 2 && superTieBreak && validSetsPerMatch === 3;
    const maxValue = isRelampagoGroupPhase ? 99 : (isSuperTieBreakSet ? 15 : effectiveMaxGamesPerSet);
    if (numValue !== '' && (numValue < 0 || numValue > maxValue)) return;
    
    setSets(prev => {
      const newSets = [...prev];
      newSets[setIndex] = { ...newSets[setIndex], [team]: numValue };
      return newSets;
    });
    
    setErrors([]);
  };

  const normalizedRelevantSets = sets.slice(0, validSetsPerMatch).map(set => ({
    team1: typeof set.team1 === 'string' ? 0 : set.team1,
    team2: typeof set.team2 === 'string' ? 0 : set.team2,
  }));
  const playedSets = normalizedRelevantSets.filter(set => set.team1 > 0 || set.team2 > 0);
  const currentWinner = playedSets.length > 0 ? calculateWinner(playedSets) : null;
  
  console.log('🔢 Rendering inputs - validSetsPerMatch:', validSetsPerMatch);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!isSaving) onOpenChange(o); }}>
        <DialogContent className={cn(
          useSimplifiedUI ? "sm:max-w-[450px]" : (validSetsPerMatch === 1 ? "sm:max-w-[500px]" : "sm:max-w-[650px]"),
          compactMode && "p-4"
        )}>
        <DialogHeader>
          <DialogTitle className={cn("flex items-center gap-2", compactMode && "text-base")}>
            <Trophy className={compactMode ? "w-4 h-4" : "w-5 h-5"} />
            {useSimplifiedUI ? 'Seleccionar Ganador' : 'Asignar Score del Partido'}
          </DialogTitle>
        </DialogHeader>

        <div className={compactMode ? "space-y-2 py-2" : "space-y-4 py-4"}>
          {useSimplifiedUI ? (
            // UI SIMPLIFICADA PARA ELIMINACIÓN O LIGAS ESCALERA DOBLES
            <div className="space-y-4">
              <Label className="text-sm text-muted-foreground">
                {isDoublesLadder ? 'Selecciona la pareja ganadora' : 'Selecciona el equipo ganador'}
              </Label>
              
              <div className="grid grid-cols-1 gap-3">
                {/* Card Equipo 1 */}
                <div
                  onClick={() => setSelectedWinner('team1')}
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    "hover:border-primary hover:bg-primary/5",
                    selectedWinner === 'team1' 
                      ? "border-green-500 bg-green-50 dark:bg-green-950/30" 
                      : "border-border"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-lg">{match.team1}</span>
                    {selectedWinner === 'team1' && (
                      <Trophy className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                </div>
                
                {/* Card Equipo 2 */}
                <div
                  onClick={() => setSelectedWinner('team2')}
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    "hover:border-primary hover:bg-primary/5",
                    selectedWinner === 'team2' 
                      ? "border-green-500 bg-green-50 dark:bg-green-950/30" 
                      : "border-border"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-lg">{match.team2}</span>
                    {selectedWinner === 'team2' && (
                      <Trophy className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                </div>
              </div>
              
              {/* Winner Preview */}
              {selectedWinner && (
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 p-3 rounded-md">
                  <p className="text-sm text-green-700 dark:text-green-300">
                    <span className="font-semibold">Ganador seleccionado: </span>
                    {selectedWinner === 'team1' ? match.team1 : match.team2}
                  </p>
                </div>
              )}
            </div>
          ) : (
            // UI EXISTENTE PARA FASE DE GRUPOS CON SETS
            <>
              {/* Score Grid */}
              <div className={compactMode ? "space-y-3" : "space-y-4"}>
                <Label className="text-sm text-muted-foreground">
                  Score por Set {validSetsPerMatch === 1 ? '(1 Set)' : '(Mejor de 3)'}
                </Label>

                {/* Team 1 Block */}
                <div className={cn("flex items-center", compactMode ? "gap-2" : "gap-3")}>
                  <div className={cn(
                    "flex-1 min-h-[48px] flex items-center bg-primary/10 text-primary rounded-md font-semibold",
                    compactMode ? "px-2 py-2 text-xs" : "px-3 py-2 text-sm"
                  )}>
                    {match.team1}
                  </div>
                  <div className={cn("flex-shrink-0 flex items-end", compactMode ? "gap-1" : "gap-2")}>
                    {Array.from({ length: validSetsPerMatch }).map((_, index) => {
                      const isSuperTieBreakSet = index === 2 && superTieBreak && validSetsPerMatch === 3;
                      return (
                        <div key={`team1-set-${index}`} className="flex flex-col items-center gap-1">
                          <span className={cn(
                            "text-center font-medium",
                            compactMode ? "text-[10px]" : "text-xs",
                            isSuperTieBreakSet ? "text-amber-600 font-semibold" : "text-muted-foreground"
                          )}>
                            {isSuperTieBreakSet ? "SM" : `S${index + 1}`}
                          </span>
                          <Input
                            type="number"
                            min="0"
                            max={isRelampagoGroupPhase ? 99 : (isSuperTieBreakSet ? 15 : effectiveMaxGamesPerSet)}
                            value={sets[index]?.team1 ?? ''}
                            onChange={(e) => handleSetChange(index, 'team1', e.target.value)}
                            className={cn(
                              compactMode ? "w-12 h-12 text-lg" : "w-14 h-14 text-xl",
                              "text-center font-bold p-0",
                              isSuperTieBreakSet && "border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500"
                            )}
                            placeholder="-"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Team 2 Block */}
                <div className={cn("flex items-center", compactMode ? "gap-2" : "gap-3")}>
                  <div className={cn(
                    "flex-1 min-h-[48px] flex items-center bg-secondary/50 text-secondary-foreground rounded-md font-semibold",
                    compactMode ? "px-2 py-2 text-xs" : "px-3 py-2 text-sm"
                  )}>
                    {match.team2}
                  </div>
                  <div className={cn("flex-shrink-0 flex items-end", compactMode ? "gap-1" : "gap-2")}>
                    {Array.from({ length: validSetsPerMatch }).map((_, index) => {
                      const isSuperTieBreakSet = index === 2 && superTieBreak && validSetsPerMatch === 3;
                      return (
                        <Input
                          key={`team2-set-${index}`}
                          type="number"
                          min="0"
                          max={isRelampagoGroupPhase ? 99 : (isSuperTieBreakSet ? 15 : effectiveMaxGamesPerSet)}
                          value={sets[index]?.team2 ?? ''}
                          onChange={(e) => handleSetChange(index, 'team2', e.target.value)}
                          className={cn(
                            compactMode ? "w-12 h-12 text-lg" : "w-14 h-14 text-xl",
                            "text-center font-bold p-0",
                            isSuperTieBreakSet && "border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500"
                          )}
                          placeholder="-"
                        />
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Winner Preview */}
              {currentWinner && (
                <div className={cn("p-3 rounded-md", currentWinner === 'draw' ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800" : "bg-muted")}>
                  <p className="text-sm">
                    {currentWinner === 'draw' ? (
                      <span className="font-semibold text-amber-700 dark:text-amber-300">Empate</span>
                    ) : (
                      <>
                        <span className="font-semibold">Ganador: </span>
                        <span className="text-primary">
                          {currentWinner === 'team1' ? match.team1 : match.team2}
                        </span>
                      </>
                    )}
                  </p>
                  {playedSets.length > 0 && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Score: {formatScore(playedSets, currentWinner)}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md space-y-1">
              {errors.map((error, index) => (
                <p key={index} className="text-sm">• {error}</p>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || (strictSaveMode && (useSimplifiedUI ? !selectedWinner : !currentWinner || (currentWinner === 'draw' && !isRelampagoGroupPhase)))}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Guardando...
              </>
            ) : (
              useSimplifiedUI ? 'Confirmar Ganador' : 'Guardar Score'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScoreAssignmentDialog;
